import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useSocket } from './SocketContext';
import { useUnitSettings } from './UnitSettingsContext';
import api, {
  manualEntryAPI, settingsAPI, calendarAPI, systemsAPI, roomAPI, meterAPI,
  hydroUnitsAPI, alertsAPI, farmLogAPI, plantsAPI, issuesAPI,
} from '../services/api';
import { defaultRanges, resolveRanges, classifyReading, breachDirection } from '../config/sensorRanges';
import { SYSTEMS, systemName, systemForPlant } from '../config/systems';
import { isFresh } from '../config/freshness';
import { UNIT_RATE_INR } from '../config/meter';
import { STAGE_TEXT } from '../config/climate';

/*
 * What is wrong on the farm right now, derived once and shared.
 *
 * The Dashboard's "Needs you" list and the sidebar's badges and dots both read
 * this, so the two can never disagree about whether something needs a person.
 * Every item carries `nav`: the sidebar page where it gets fixed.
 *
 * The tanks are judged on handheld readings: no reservoir probe is installed
 * (2026-10). A probe that never reported raises nothing; one that reported this
 * week and stopped is a fault.
 */

const IST = 19800;
const DAY = 86400;
const REFRESH_MS = 60000;
// A handheld reading this old means the tank was not checked, whatever it said.
const MANUAL_STALE_DAYS = 2;
// Container average this far over goal is worth a person's attention, but only while the
// AC is not already cooling below the goal: with all lamps on it sits at its floor from
// ~10:00 to lights-off and the container still runs 2-5° over (Oct 2026), so "over goal"
// alone fired every afternoon and nobody could act on it.
export const CLIMATE_WARM_C = 2;
// The plants' own lines, from research.md: >= 26 °C costs Murano yield (heat_break_rule
// HARD_C); 25 °C is the warning one degree before it. Judged on the front end, the warm one.
export const FRONT_HOT_C = 25;
export const FRONT_DAMAGE_C = 26;
// The lowest setpoint the balancer sends (backend AC_TEMP_MIN): at it the AC is doing all
// it can even when the goal is also 16 °C.
export const AC_FLOOR_C = 16;
// A plant's latest log decides its health however old it is: a plant stays stressed
// until someone logs otherwise (a 21-day cut-off hid 4 stressed plants, 2026-10-01).
// Past this age the Needs you line says how old the observation is.
const PLANT_LOG_OLD_DAYS = 14;
// A spray is "due" only while it is still in use: past two missed cycles plus a week
// it is treated as dropped, or a product sprayed once would nag forever.
const SPRAY_GRACE_DAYS = 7;

export const SEVERITY_RANK = { critical: 0, warning: 1, notice: 2 };

const STAGE_LABELS = {
  planting: 'Planting', establishment: 'Establishment', vegetative: 'Vegetative',
  budding: 'Budding', flowering: 'Flowering', fruiting: 'Fruiting', harvest: 'Harvest',
};
export const stageLabel = key => STAGE_LABELS[key] || key || '';
// One colour per stage (Plant Viewer, Dashboard); never the health green / amber / red.
export const stageColor = (theme, s) => ({
  establishment: theme.colors.sprout,
  vegetative: theme.colors.accent,
  budding: theme.colors.water,
  flowering: theme.colors.signature,
  fruiting: theme.colors.temperature,
  harvest: theme.colors.co2,
}[s] || theme.colors.offline);

const ALL_UNITS = SYSTEMS.map(({ id, name }) => ({ id, name }));
const UNIT_NAMES = Object.fromEntries(ALL_UNITS.map(u => [u.id, u.name]));

export const nowSec = () => Math.floor(Date.now() / 1000);
export const istDayStart = ts => Math.floor((ts + IST) / DAY) * DAY - IST;
export const num = v => (typeof v === 'number' && !Number.isNaN(v) ? v : null);
export const listNames = names => (names.length <= 2 ? names.join(' and ')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

// "9:33 am", with "yesterday" or the date when it is not today.
export function clock(ts, now = nowSec()) {
  if (!ts) return '';
  const d = new Date((ts + IST) * 1000);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const t = `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'am' : 'pm'}`;
  if (istDayStart(ts) === istDayStart(now)) return t;
  if (istDayStart(ts) === istDayStart(now) - DAY) return `yesterday ${t}`;
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}, ${t}`;
}

// Which sidebar page fixes a device that went quiet.
const deviceNav = key => (key === 'meter' ? '/power' : key.startsWith('room:') ? '/rooms' : '/hydro-units');

const FarmStatusContext = createContext(null);

export function FarmStatusProvider({ children }) {
  const { sensorUpdateTS, roomUpdateTS, sensorData: socketSensors, roomData: socketRooms, relayData: socketRelays, issueUpdate } = useSocket();
  const { isUnitEnabled, isUnitInMaintenance } = useUnitSettings();
  const [now, setNow] = useState(nowSec());
  const [loaded, setLoaded] = useState(false);
  const [probes, setProbes] = useState({});
  const [rooms, setRooms] = useState({ front: null, back: null });
  const [data, setData] = useState({});
  const [lights, setLightsState] = useState({ units: {}, reports: {} });

  const enabledUnits = useMemo(() => ALL_UNITS.filter(u => isUnitEnabled(u.id)), [isUnitEnabled]);

  // The clock that ages every "N min ago" and decides staleness.
  useEffect(() => {
    const id = setInterval(() => setNow(nowSec()), 30000);
    return () => clearInterval(id);
  }, []);

  // Everything the rules need, every minute. allSettled and applied one by one, so
  // one unhappy endpoint blanks only its own part.
  useEffect(() => {
    let cancelled = false;
    const sources = {
      manual: () => manualEntryAPI.getStats().then(r => r.data?.stats || {}),
      ranges: () => settingsAPI.getRanges().then(r => resolveRanges(r.data?.ranges)),
      water: () => calendarAPI.getWaterSchedule().then(r => r.data),
      crop: () => systemsAPI.getCrop().then(r => r.data),
      ac: () => roomAPI.getACTarget().then(r => r.data),
      meter: () => meterAPI.getLatest().then(r => r.data),
      alerts: () => alertsAPI.get().then(r => r.data),
      sprays: () => farmLogAPI.getSpraySchedule().then(r => r.data?.schedule || []),
      plants: () => plantsAPI.getLatest({ limit: 200 }).then(r => r.data?.plants || []),
      relays: () => hydroUnitsAPI.getRelaysOverview().then(r => r.data?.units || []),
      relayStatus: () => hydroUnitsAPI.getRelayDeviceStatus().then(r => r.data?.units || []),
      front: () => roomAPI.getFrontSensors().then(r => r.data),
      back: () => roomAPI.getBackSensors().then(r => r.data),
      dehum: () => roomAPI.getDehumidifier().then(r => r.data),
      issues: () => issuesAPI.list({ status: 'active' }).then(r => r.data?.issues || []),
      probes: () => Promise.all(ALL_UNITS.map(u => api.get(`/units/${u.id}/sensors`).then(r => [u.id, r.data])))
        .then(Object.fromEntries),
    };
    const load = async () => {
      const keys = Object.keys(sources);
      const results = await Promise.allSettled(keys.map(k => sources[k]()));
      if (cancelled) return;
      const got = {};
      results.forEach((res, i) => { if (res.status === 'fulfilled') got[keys[i]] = res.value; });
      setData(prev => ({ ...prev, ...got }));
      if (got.front !== undefined || got.back !== undefined) {
        setRooms(prev => ({ front: got.front ?? prev.front, back: got.back ?? prev.back }));
      }
      if (got.probes) setProbes(prev => ({ ...prev, ...got.probes }));
      if (got.relays) {
        const units = Object.fromEntries(got.relays.map(u => [u.unit_id, u]));
        const reports = Object.fromEntries((got.relayStatus || []).filter(u => u.relay === 'lights').map(u => [u.unit_id, u]));
        setLightsState(prev => ({ units, reports: got.relayStatus ? reports : prev.reports }));
      }
      setLoaded(true);
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Open issues change by hand, not on a clock: refetch on each push rather than
  // waiting for the next poll, so the Dashboard card and the badge follow at once.
  useEffect(() => {
    if (!issueUpdate) return undefined;
    let cancelled = false;
    issuesAPI.list({ status: 'active' })
      .then(r => { if (!cancelled) setData(prev => ({ ...prev, issues: r.data?.issues || [] })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [issueUpdate]);

  // Live pushes between polls.
  useEffect(() => {
    if (!socketSensors) return;
    setProbes((prev) => {
      const next = { ...prev };
      Object.entries(socketSensors).forEach(([id, d]) => { if (d.reservoir) next[id] = { ...next[id], ...d }; });
      return next;
    });
  }, [sensorUpdateTS, socketSensors]);

  useEffect(() => {
    if (!socketRooms) return;
    setRooms(prev => ({
      front: socketRooms.ROOM_FRONT ? { ...prev.front, ...socketRooms.ROOM_FRONT } : prev.front,
      back: socketRooms.ROOM_BACK ? { ...prev.back, ...socketRooms.ROOM_BACK } : prev.back,
    }));
  }, [roomUpdateTS, socketRooms]);

  useEffect(() => {
    if (!socketRelays) return;
    setLightsState((prev) => {
      const units = { ...prev.units };
      Object.entries(socketRelays).forEach(([id, u]) => {
        if (!units[id]) return;
        units[id] = {
          ...units[id],
          relays: { ...units[id].relays, ...u.relays },
          control_modes: u.control_modes || units[id].control_modes,
        };
      });
      return { ...prev, units };
    });
  }, [socketRelays]);

  // The Dashboard's light controls hold the freshest copy (they refetch after
  // every change); they hand it over so the sidebar updates at once.
  // Ignored while they are still loading (empty), so they never blank it.
  const setLights = useCallback((units, reports) => {
    if (Object.keys(units || {}).length) setLightsState({ units, reports });
  }, []);

  const ranges = data.ranges || defaultRanges;
  const todayStart = istDayStart(now);

  const tanks = useMemo(() => enabledUnits.map((u) => {
    const m = data.manual?.[u.id] || {};
    const ph = num(m.ph?.latest);
    const ec = num(m.ec?.latest);
    const lastAt = Math.max(m.ph?.latest_timestamp || 0, m.ec?.latest_timestamp || 0) || null;
    const probe = probes[u.id];
    const probeLive = !!probe?.reservoir && isFresh(probe.timestamp);
    const water = (data.water?.schedule || []).find(s => s.system_id === u.id) || null;
    const current = !!lastAt && now - lastAt <= MANUAL_STALE_DAYS * DAY;
    return {
      ...u, ph, ec, lastAt, water, current,
      maintenance: isUnitInMaintenance(u.id),
      checkedToday: !!lastAt && lastAt >= todayStart,
      phLevel: current ? classifyReading('ph', ph, ranges) : null,
      ecLevel: current ? classifyReading('ec', ec, ranges) : null,
      probe: probeLive ? probe.reservoir : null,
      probeAt: probe?.timestamp || null,
    };
  }), [enabledUnits, data.manual, data.water, probes, ranges, now, todayStart, isUnitInMaintenance]);

  const climate = useMemo(() => {
    const ends = [['front', rooms.front], ['back', rooms.back]]
      .filter(([, r]) => r && isFresh(r.timestamp))
      .map(([k, r]) => ({ k, temp: num(r.bme?.temp), rh: num(r.bme?.humidity) }));
    const mean = vals => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null);
    const ac = data.ac;
    const goal = num(ac?.goal_temp);
    const temp = mean(ends.map(e => e.temp).filter(v => v !== null));
    const rh = mean(ends.map(e => e.rh).filter(v => v !== null));
    const stage = String(ac?.stage || '').split('+')[0];
    return {
      temp, rh, goal,
      front: ends.find(e => e.k === 'front')?.temp ?? null,
      error: temp !== null && goal !== null ? temp - goal : null,
      setpoint: num(ac?.temp),
      balancer: ac?.balancer_enabled !== false,
      stageText: STAGE_TEXT[stage]?.[0] || null,
      rhLevel: classifyReading('humidity', rh, ranges),
    };
  }, [rooms, data.ac, ranges]);

  const power = useMemo(() => {
    const m = data.meter;
    if (!m) return null;
    return {
      status: m.status,
      watts: m.status === 'online' ? num(m.totals?.power_w) : null,
      soFar: m.energy?.today_kwh ?? null,
      cost: m.energy?.month_kwh != null ? m.energy.month_kwh * UNIT_RATE_INR : null,
      breakerOpen: m.basic?.switch_on === false,
    };
  }, [data.meter]);

  const lightSum = useMemo(() => {
    const ids = Object.keys(lights.units).filter(isUnitEnabled);
    const mode = id => lights.units[id]?.control_modes?.lights;
    const win = id => lights.units[id]?.schedule?.lights;
    return {
      ids,
      on: ids.filter(id => lights.units[id]?.relays?.lights === 'ON'),
      manual: ids.filter(id => mode(id) === 'manual'),
      resting: ids.filter(id => lights.units[id]?.heat_break_until),
      unset: ids.filter(id => mode(id) !== 'manual' && !(win(id)?.on && win(id)?.off)),
      unconfirmed: ids.filter((id) => {
        const r = lights.reports[id];
        return r && r.online && (!r.confirmed || !r.applied);
      }),
    };
  }, [lights, isUnitEnabled]);

  const devices = useMemo(() => {
    const list = data.alerts?.devices || [];
    return {
      list,
      offline: list.filter(d => !d.online),
      bot: data.alerts?.bot || null,
      groupSet: !!data.alerts?.config?.group_jid,
    };
  }, [data.alerts]);

  // The oldest stressed / unhealthy log among the plants /plants/latest returns (it
  // is capped at 200, so this is best-effort and only used to say how old).
  const freshHealth = useMemo(() => {
    if (!data.plants) return null;
    const by = {};
    data.plants.forEach((pl) => {
      const sys = systemForPlant(pl.plant_id)?.id;
      if (!sys) return;
      const e = by[sys] || (by[sys] = { stressed: 0, unhealthy: 0, oldest: null });
      const h = pl.health_status;
      if (h !== 'unhealthy' && h !== 'stressed' && h !== 'minor_issues') return;
      if (h === 'unhealthy') e.unhealthy += 1; else e.stressed += 1;
      if (pl.timestamp && (e.oldest == null || pl.timestamp < e.oldest)) e.oldest = pl.timestamp;
    });
    return by;
  }, [data.plants]);

  const crop = useMemo(() => {
    const order = data.crop?.stage_order || [];
    const rows = (data.crop?.systems || [])
      .filter(s => isUnitEnabled(s.system_id) && (s.planted_at || s.plants?.total > 0))
      .map((s) => {
        const obs = s.observed_stage === 'planting' ? 'establishment' : s.observed_stage;
        const oi = obs ? order.indexOf(obs) : -1;
        const ei = s.expected_stage ? order.indexOf(s.expected_stage) : -1;
        // Counts come from /systems/crop, which covers every plant. /plants/latest is
        // capped at 200 rows, so counting from it showed "442 of 442 healthy" while
        // 4 were stressed outside that page (2026-10-01); it is used only for ages.
        const h = s.plants?.health || {};
        const stressed = (h.stressed || 0) + (h.minor_issues || 0);
        const unhealthy = h.unhealthy || 0;
        return {
          ...s,
          name: systemName(s.system_id),
          drift: oi >= 0 && ei >= 0 ? oi - ei : null,
          healthy: Math.max(0, (s.plants?.total || 0) - stressed - unhealthy),
          stressed,
          unhealthy,
        };
      });
    const sum = k => rows.reduce((a, r) => a + r[k], 0);
    const sumP = k => rows.reduce((a, r) => a + (r.plants?.[k] || 0), 0);
    // every plant's latest stage, all systems together (planting counts as establishment)
    const stages = {};
    rows.forEach(r => Object.entries(r.plants?.stages || {}).forEach(([st, n]) => {
      const k = st === 'planting' ? 'establishment' : st;
      stages[k] = (stages[k] || 0) + n;
    }));
    const lastChecks = rows.map(r => r.plants?.last_check).filter(Boolean);
    const days = rows.map(r => r.day).filter(d => d != null);
    const harvest = rows.map(r => r.harvest?.days_away).filter(d => d != null);
    return {
      rows, order,
      total: rows.reduce((a, r) => a + (r.plants?.total || 0), 0),
      healthy: sum('healthy'), stressed: sum('stressed'), unhealthy: sum('unhealthy'),
      behind: rows.filter(r => r.drift !== null && r.drift < 0),
      stages,
      worse: sumP('worse'), better: sumP('better'), checkedWeek: sumP('checked_week'),
      lastCheck: lastChecks.length ? Math.max(...lastChecks) : null,
      withFlowers: sumP('with_flowers'), flowers: sumP('flowers'),
      withBuds: sumP('with_buds'), buds: sumP('buds'),
      withFruit: sumP('with_fruit'), fruit: sumP('fruit'),
      newToday: ['buds', 'flowers', 'fruit', 'lost', 'removed', 'plants_checked',
        'started_budding', 'started_flowering', 'started_fruiting'].reduce((o, k) => ({
        ...o, [k]: rows.reduce((a, r) => a + (r.plants?.new_today?.[k] || 0), 0),
      }), {}),
      day: days.length ? Math.max(...days) : null,
      harvestIn: harvest.length ? Math.min(...harvest) : null,
      variety: rows.find(r => r.variety)?.variety || null,
    };
  }, [data.crop, isUnitEnabled]);

  /* What needs a person. `to` is where the row links; `nav` is the sidebar page
     that carries the badge (the same page, or the nearest one for a device). */
  const items = useMemo(() => {
    const out = [];
    // `kind` groups same-cause items for display (four racks overdue is one row);
    // `short` is how one member reads inside such a group ("Rack A 40 d").
    const add = (severity, subject, text, to, fix, nav = to, kind = text, short = subject) => out.push({
      severity, subject, text, to, fix, nav, kind, short, id: `${subject}|${text}`,
    });

    if (power?.breakerOpen) add('critical', 'Power', 'breaker is open, the farm has no supply', '/power', 'Power', '/power', 'breaker');

    devices.offline.forEach(d => add('warning', d.label, `offline since ${clock(d.last_seen, now)}`, null, null, deviceNav(d.key), 'device', `${d.label}, since ${clock(d.last_seen, now)}`));
    if (devices.bot && !devices.bot.connected) {
      add('warning', 'WhatsApp alerts', devices.bot.needs_qr ? 'bot is unlinked, nothing reaches the group' : 'bot is not connected, alerts are queuing', null, null, '/settings', 'bot');
    }

    tanks.forEach((t) => {
      if (t.maintenance) return;
      [['pH', 'ph', t.ph, t.phLevel, v => v.toFixed(1)], ['EC', 'ec', t.ec, t.ecLevel, v => v.toFixed(2)]]
        .forEach(([label, key, v, level, f]) => {
          if (level) add(level, t.name, `${label} ${f(v)} is ${breachDirection(key, v, ranges)} the safe range`, '/manual-entry', 'Log a fix', '/manual-entry', 'tank', `${t.name} ${label} ${f(v)}`);
        });
      if (t.probe) {
        ['ph', 'water_temp'].forEach((key) => {
          const v = num(t.probe[key]);
          const level = classifyReading(key, v, ranges);
          if (level) add(level, t.name, `probe ${key === 'ph' ? 'pH' : 'water temp'} ${v.toFixed(1)} is ${breachDirection(key, v, ranges)} the safe range`, `/hydro-units/${t.id}`, 'Open', '/hydro-units', 'probe-range');
        });
      }
      if (!t.probe && t.probeAt && now - t.probeAt < 7 * DAY) {
        add('warning', t.name, `tank probe silent since ${clock(t.probeAt, now)}`, `/hydro-units/${t.id}`, 'Open', '/hydro-units', 'probe-silent', `${t.name}, since ${clock(t.probeAt, now)}`);
      }
      if (!t.current) {
        add('notice', t.name, t.lastAt ? `not checked for ${Math.floor((now - t.lastAt) / DAY)} days` : 'never checked by hand', '/manual-entry', 'Log reading',
          '/manual-entry', 'unchecked', t.lastAt ? `${t.name}\u00a0${Math.floor((now - t.lastAt) / DAY)}\u00a0d` : `${t.name}\u00a0never`);
      }
      if (t.water?.is_overdue) {
        const interval = t.water.interval_days || data.water?.interval_days || 14;
        add(t.water.days_since_change >= interval * 2 ? 'critical' : 'warning', t.name,
          `water change overdue, last one ${t.water.days_since_change} days ago`, '/nutrients', 'Nutrients',
          '/nutrients', 'water', `${t.name}\u00a0${t.water.days_since_change}\u00a0d`);
      }
    });

    if (climate.front !== null && climate.front >= FRONT_HOT_C) {
      add(climate.front >= FRONT_DAMAGE_C ? 'critical' : 'warning', 'Container',
        `front end ${climate.front.toFixed(1)} °C, ${climate.front >= FRONT_DAMAGE_C ? 'at' : 'near'} the ${FRONT_DAMAGE_C} °C line where Murano loses yield`,
        '/rooms', 'Environment', '/rooms', 'climate');
    } else if (climate.error !== null && climate.error >= CLIMATE_WARM_C
               && climate.setpoint !== null && climate.setpoint >= climate.goal && climate.setpoint > AC_FLOOR_C) {
      // Over goal and the AC is not being pushed below it: balancer off, or the AC is
      // not taking commands. While it escalates (setpoint under goal) it is doing its job.
      add('warning', 'Container',
        `${climate.temp.toFixed(1)} °C, ${climate.error.toFixed(1)}° over the ${climate.goal} °C goal and the AC is only set to ${climate.setpoint} °C`,
        '/rooms', 'Environment', '/rooms', 'climate');
    }
    if (climate.rhLevel) {
      add(climate.rhLevel, 'Container', `humidity ${Math.round(climate.rh)} % is ${breachDirection('humidity', climate.rh, ranges)} the safe range`, '/rooms', 'Environment', '/rooms', 'humidity');
    }
    // Auto humidity's one item (fault, out of reach while humid, damp for 15 min),
    // decided on the server so the page, this list and WhatsApp say the same thing.
    const dn = data.dehum?.needs;
    if (dn) add(dn.severity, dn.kind === 'damp' ? 'Container' : 'Dehumidifier', dn.detail, '/rooms', 'Environment', '/rooms', `dehum-${dn.kind}`);
    // The auto-balancer being off is a setting, not a problem: it shows on Environment
    // and the Container card, not here, or it would sit in this list for as long as
    // someone chooses to run on the schedule.

    lightSum.unconfirmed.forEach(id => add('warning', UNIT_NAMES[id] || id, 'light switch not confirmed by the controller', '/hydro-units', 'Hydro units', '/hydro-units', 'light-unconfirmed'));
    // One line per cause, not per rack: five racks with the same problem is one fix.
    const racks = ids => listNames(ids.map(id => UNIT_NAMES[id] || id));
    if (lightSum.manual.length) add('notice', 'Lights', `on manual on ${racks(lightSum.manual)}, the timer is not running`, '/hydro-units', 'Hydro units', '/hydro-units', 'light-manual');
    if (lightSum.unset.length) add('notice', 'Lights', `no timer window saved on ${racks(lightSum.unset)}, nothing switches them`, '/hydro-units', 'Hydro units', '/hydro-units', 'light-unset');

    (data.sprays || [])
      .filter(s => s.is_overdue && s.days_since_application != null
        && s.days_since_application <= (s.frequency_days || 0) * 2 + SPRAY_GRACE_DAYS)
      .forEach(s => add('notice', s.name, `spray is due, last ${s.days_since_application} d ago`, '/spray-log', 'Spray log', '/spray-log', 'spray'));

    if (crop.unhealthy > 0 || crop.stressed > 0) {
      const parts = [crop.unhealthy && `${crop.unhealthy} unhealthy`, crop.stressed && `${crop.stressed} stressed`].filter(Boolean);
      const oldest = Math.min(...Object.values(freshHealth || {}).map(e => e.oldest).filter(Boolean));
      const age = Number.isFinite(oldest) ? Math.floor((now - oldest) / DAY) : null;
      add(crop.unhealthy > 0 ? 'warning' : 'notice', 'Plants',
        `${parts.join(', ')}${age != null && age > PLANT_LOG_OLD_DAYS ? `, oldest log ${age} d ago` : ''}`,
        '/plant-viewer', 'Plant viewer', '/plant-viewer', 'plants');
    }
    crop.behind.forEach(r => add('notice', r.name, `crop behind schedule, expected ${stageLabel(r.expected_stage).toLowerCase()}`, '/calendar', 'Calendar', '/calendar', 'crop'));

    return out.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  }, [power, devices, tanks, climate, lightSum, data.sprays, data.water, data.dehum, crop, freshHealth, ranges, now]);

  /* Per sidebar page: how many problems a person should act on (warning and
     critical) and the worst severity, including notices. Monitor pages also get
     a live state, so a quiet green dot means "reporting and fine". */
  const nav = useMemo(() => {
    const map = {};
    items.forEach((i) => {
      if (!i.nav) return;
      const e = map[i.nav] || (map[i.nav] = { count: 0, worst: null });
      if (i.severity !== 'notice') e.count += 1;
      if (!e.worst || SEVERITY_RANK[i.severity] < SEVERITY_RANK[e.worst]) e.worst = i.severity;
    });
    const urgent = items.filter(i => i.severity !== 'notice');
    map['/'] = { count: urgent.length, worst: urgent[0]?.severity || null };
    // Issues are not in `items` (they have their own Dashboard card, and would only
    // repeat there); the badge counts every open issue, red if any is high severity.
    const open = data.issues || [];
    if (open.length) {
      map['/issues'] = { count: open.length, worst: open.some(i => i.severity === 'high') ? 'critical' : 'warning' };
    }
    const live = {
      '/rooms': climate.temp !== null,
      '/power': power?.status === 'online',
      '/hydro-units': lightSum.ids.length > 0,
    };
    Object.entries(live).forEach(([path, ok]) => {
      const e = map[path] || (map[path] = { count: 0, worst: null });
      e.state = e.worst && e.worst !== 'notice' ? e.worst : ok ? 'normal' : 'offline';
    });
    return map;
  }, [items, climate.temp, power, lightSum.ids.length, data.issues]);

  const value = {
    loaded, now, ranges, data, rooms, lights,
    tanks, climate, power, lightSum, devices, crop, items, nav, setLights,
  };
  return <FarmStatusContext.Provider value={value}>{children}</FarmStatusContext.Provider>;
}

export const useFarmStatus = () => useContext(FarmStatusContext);
